import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ConfigProvider from '../../config-provider';
import Search from '../Search';
import Animate from '../../animate';
import Balloon from '../../balloon';

import Button from '../../button';
import { isFunction } from '../../util/func';

const { Tooltip } = Balloon;

const IconSearchFC = React.memo(
  ({
    prefix,
    className,
    disabled,
    onFocus,
    onChange,
    value: propsValue,
    onBlur,
    direction,
    searchCls: propsSearchCls,
    open,
    onOpen,
    triggerTooltipProps,
    ...searchProps
  }) => {
    const [value, setValue] = useState(propsValue);
    const searchRef = useRef(null);
    const rootCls = `${prefix}iconsearch`;
    const wrapperCls = classNames(rootCls, className, {
      rtl: direction === 'rtl',
    });
    const searchCls = classNames(`${rootCls}-search`, propsSearchCls, {
      [`${rootCls}-fold`]: !open,
    });

    const handleFocus = useCallback(
      e => {
        onOpen(true);
        onFocus && onFocus(e);
      },
      [onFocus, onOpen],
    );
    const handleBlur = useCallback(
      e => {
        if (value === '' || value === undefined) {
          onOpen(false);
        }
        onBlur && onBlur(e);
      },
      [onBlur, value, onOpen],
    );
    const handleClick = useCallback(() => {
      if (!open) {
        onOpen(true);
        return;
      }
      setTimeout(() => {
        if (searchRef.current && searchRef.current.getInstance) {
          const instance = searchRef.current.getInstance();
          instance && instance.focus && instance.focus();
        }
      }, 50);
    });
    const handleChange = useCallback(
      (v, ...argv) => {
        setValue(v);
        onChange && onChange(v, ...argv);
      },
      [onChange],
    );
    useEffect(() => setValue(propsValue), [propsValue]);
    useEffect(() => {
      open && handleClick();
    }, [open, handleClick]);

    let iconButton = (
      <Button.Icon
        name="search"
        type="primary"
        color="gray"
        disabled={disabled}
        className={`${rootCls}-icon`}
        onClick={handleClick}
      />
    );

    if (triggerTooltipProps) {
      iconButton = (
        <Tooltip {...triggerTooltipProps} trigger={iconButton}>
          {triggerTooltipProps.children}
        </Tooltip>
      );
    }

    const animation = {
      enter: `${rootCls}-fade-in`,
      leave: `${rootCls}-fade-out`,
    };

    return (
      <div className={wrapperCls}>
        <Animate animation={animation}>{open ? null : iconButton}</Animate>
        <Search
          {...searchProps}
          ref={searchRef}
          disabled={disabled}
          className={searchCls}
          onFocus={handleFocus}
          value={value}
          onBlur={handleBlur}
          onChange={handleChange}
          type="normal"
        />
      </div>
    );
  },
);

class IconSearch extends React.Component {
  state = {
    open: false,
  };

  static propTypes = {
    prefix: PropTypes.string,
    direction: PropTypes.oneOf(['ltr', 'rtl']),
    searchCls: PropTypes.string,
    triggerTooltipProps: PropTypes.object,
    onOpenedChange: PropTypes.func,
  };

  static defaultProps = {
    prefix: 'next-',
    direction: 'ltr',
  };

  open() {
    this.handleOpenChange(true);
  }

  close() {
    this.handleOpenChange(false);
  }

  handleOpenChange = open => {
    this.setState({ open });

    if (isFunction(this.props.onOpenedChange)) {
      this.props.onOpenedChange(open);
    }
  };

  render() {
    return <IconSearchFC {...this.props} open={this.state.open} onOpen={this.handleOpenChange} />;
  }
}

export default ConfigProvider.config(IconSearch);
